/*
 * The outbound queue: what this device did while it could not reach the server.
 *
 * ────────────────────────────────────────────────────────────────────────
 * THIS IS NOT Field.js's DRAFT
 * ────────────────────────────────────────────────────────────────────────
 *
 * Field.js keeps ONE in-progress form in localStorage so a phone that dies
 * mid-collection does not lose what was typed. That is form recovery: one
 * record, not yet finished, belonging to the screen that is open.
 *
 * This is the opposite end. These are FINISHED actions, already accepted by
 * the person, waiting for a network. They must survive a tab close, a reboot
 * and a month in a drawer, and there may be hundreds. Hence IndexedDB rather
 * than localStorage: localStorage is synchronous, roughly 5 MB, and stringly
 * typed, and a field day of collections with their metadata will exceed it.
 *
 * ────────────────────────────────────────────────────────────────────────
 * updated_at IS STAMPED WHEN THE PERSON ACTS, NOT WHEN THE SYNC RUNS
 * ────────────────────────────────────────────────────────────────────────
 *
 * The server resolves conflicts by last-write-wins on `updated_at`. If this
 * queue stamped at flush time, an edit made in a field on Monday would arrive
 * on Friday carrying Friday's timestamp and would beat a correction somebody
 * made in the lab on Wednesday. The person who typed most recently would lose
 * to the person who reconnected most recently, which is not what anyone
 * means by "last write".
 *
 * So `queuedAt` is the moment of the act, and it is what goes to the server.
 *
 * ────────────────────────────────────────────────────────────────────────
 * THE BACKEND IS INJECTABLE
 * ────────────────────────────────────────────────────────────────────────
 *
 * jsdom has no IndexedDB, and adding fake-indexeddb to ship one more package
 * to a phone to test a queue is the wrong trade. The queue takes a backend;
 * the browser passes IndexedDB, the tests pass a Map. The IndexedDB adapter
 * is kept thin enough to read in one sitting for exactly that reason.
 */

const DB_NAME = 'pitriot-sync';
const DB_VERSION = 1;
const STORE = 'outbox';
const CONFLICTS = 'conflicts';

/** A Map behind the same interface, for tests and for a browser with no IDB. */
export function memoryBackend() {
  const stores = { [STORE]: new Map(), [CONFLICTS]: new Map() };
  return {
    async getAll(store) { return [...stores[store].values()]; },
    async put(store, entry) { stores[store].set(entry.id, entry); return entry; },
    async remove(store, ids) { ids.forEach((id) => stores[store].delete(id)); },
    async clear(store) { stores[store].clear(); },
  };
}

/** IndexedDB, with every call wrapped so a blocked or private-mode store
 *  degrades to "nothing queued" rather than throwing into a click handler. */
export function indexedDbBackend(factory = (typeof indexedDB !== 'undefined' ? indexedDB : null)) {
  if (!factory) return memoryBackend();

  const open = () => new Promise((resolve, reject) => {
    const request = factory.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(CONFLICTS)) db.createObjectStore(CONFLICTS, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  const run = (store, mode, work) => open().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const result = work(tx.objectStore(store));
    tx.oncomplete = () => resolve(result && result.result !== undefined ? result.result : result);
    tx.onerror = () => reject(tx.error);
  }));

  return {
    getAll: (store) => run(store, 'readonly', (s) => s.getAll()),
    put: (store, entry) => run(store, 'readwrite', (s) => s.put(entry)),
    remove: (store, ids) => run(store, 'readwrite', (s) => { ids.forEach((id) => s.delete(id)); }),
    clear: (store) => run(store, 'readwrite', (s) => s.clear()),
  };
}

let identity = 0;
const newId = () => `${Date.now()}-${(identity += 1)}-${Math.random().toString(36).slice(2, 8)}`;

export function createQueue(backend = indexedDbBackend()) {
  const safely = async (work, fallback) => {
    try { return await work(); } catch { return fallback; }
  };

  return {
    /**
     * Record one finished action.
     *
     * `record` must carry its table's primary key; without one the server
     * cannot tell an insert from an update and refuses the whole batch.
     */
    async enqueue(table, record, { now = () => new Date().toISOString() } = {}) {
      if (!table || !record || typeof record !== 'object') {
        throw new Error('enqueue needs a table and a record');
      }
      const queuedAt = record.updated_at || now();
      const entry = {
        id: newId(),
        table,
        record: { ...record, updated_at: queuedAt },
        queuedAt,
      };
      await safely(() => backend.put(STORE, entry), null);
      return entry;
    },

    async pending() {
      const all = await safely(() => backend.getAll(STORE), []);
      return all.sort((a, b) => String(a.queuedAt).localeCompare(String(b.queuedAt)));
    },

    async count() {
      return (await this.pending()).length;
    },

    async forget(ids) {
      if (ids && ids.length) await safely(() => backend.remove(STORE, ids), null);
    },

    async conflicts() {
      return safely(() => backend.getAll(CONFLICTS), []);
    },

    async recordConflicts(list) {
      for (const conflict of list || []) {
        await safely(() => backend.put(CONFLICTS, {
          id: `${conflict.table}:${conflict.pk}`,
          ...conflict,
          seenAt: new Date().toISOString(),
        }), null);
      }
    },

    async dismissConflict(id) {
      await safely(() => backend.remove(CONFLICTS, [id]), null);
    },

    async clear() {
      await safely(() => backend.clear(STORE), null);
      await safely(() => backend.clear(CONFLICTS), null);
    },
  };
}

/*
 * Parents before children, for the tables that have that relationship.
 *
 * /sync applies a whole payload inside ONE transaction, table by table, in the
 * order the keys arrive, and the foreign keys are immediate. A
 * sample_vegetation row whose sample_id is a field_collection in the same
 * payload fails unless the collection was written first -- and that failure is
 * a 500 that rolls the entire batch back. Since nothing is forgotten without a
 * 2xx, it would then fail again on every subsequent flush, forever.
 *
 * Queue order alone does not give this. It is the order things were DONE, so
 * one vegetation edit from last week puts the child table first and takes
 * today's new collection down with it.
 *
 * Tables not named here follow, keeping their queue order (Array.sort is
 * stable). This list only has to grow when a queued table gains a queued
 * parent.
 */
export const TABLE_ORDER = ['field_collection', 'sample_vegetation', 'sample_phenotype'];

/**
 * Collapse the queue into one record per row, newest wins.
 *
 * Three offline edits to one collection are three entries, and sending all
 * three would have the server apply the first, then reject the second and
 * third as older -- two conflicts reported for something that is not a
 * conflict at all, just this device catching up with itself. The newest is
 * the only one the server would have kept anyway.
 *
 * Returns { changes, entryIds } -- the payload, and every entry it covers, so
 * a successful sync can forget all three.
 */
export function collapse(entries, { maxPerTable = 2000 } = {}) {
  const groups = new Map();
  for (const entry of entries) {
    const key = `${entry.table}:${primaryKeyOf(entry.record) ?? entry.id}`;
    const group = groups.get(key) || { table: entry.table, ids: [], newest: null };
    group.ids.push(entry.id);
    if (!group.newest || String(entry.queuedAt) >= String(group.newest.queuedAt)) {
      group.newest = entry;
    }
    groups.set(key, group);
  }

  const changes = {};
  const entryIds = [];
  for (const group of groups.values()) {
    const bucket = (changes[group.table] = changes[group.table] || []);
    // Over the server's per-table cap: leave the rest queued for the next run
    // rather than have the whole batch refused with a 413.
    if (bucket.length >= maxPerTable) continue;
    bucket.push(group.newest.record);
    entryIds.push(...group.ids);
  }

  const rank = (table) => {
    const at = TABLE_ORDER.indexOf(table);
    return at === -1 ? TABLE_ORDER.length : at;
  };
  const ordered = {};
  for (const table of Object.keys(changes).sort((a, b) => rank(a) - rank(b))) {
    ordered[table] = changes[table];
  }
  return { changes: ordered, entryIds };
}

/** A record's primary key, whatever the table calls it. */
export function primaryKeyOf(record) {
  if (!record) return null;
  const named = Object.keys(record).find((k) => k === 'id' || k.endsWith('_id'));
  return named ? record[named] : null;
}
